'use client';

import { z } from 'zod';
import { FieldErrors, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormEvent, useEffect } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.component';
import { useLoading } from '@/hooks/use-loading.hook';
import { cpfValidator } from '@/utils/validators';
import { setRegistrationStorage } from '@/storage/registration.storage';
import type { CreateAccountForm } from '@/types/create-account-form.types';
import { setAuthTokens } from '@/storage/auth-tokens.storage';
import { StepKeys } from '@/app/(main)/_components/sign-in-modal/_types/header-auth-modal-step-keys.enum';
import { isPendingEmailValidation, login } from '@/services/auth/login.service';
import { formatISO8601toBRDate } from '@/utils/formatters/format-iso-8601-to-br-date';
import { resolveAuthenticatedUser } from '@/store/auth.store';
import { modalsOpeningStore, setOpenSignInModal } from '@/store/modal';
import { useFocus } from '@/hooks/use-focus.hook';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useToast } from '@/components/ui/use-toast';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { HttpException } from '@/utils/api/api-client';
import {
  LoginAnalyticsEnum,
  LoginErrorType,
} from '@/types/enums/analytics/login/login-analytics.enum';

const loginFormSchema = z.object({
  documentNumber: z.custom<string>(cpfValidator, {
    message: 'CPF inválido.',
  }),
  password: z.string().min(1, {
    message: 'Informe sua senha.',
  }),
});

type LoginForm = z.infer<typeof loginFormSchema>;

const defaultValues: Partial<LoginForm> = {
  documentNumber: '',
  password: '',
};

export default function LoginForm() {
  const loginCallback = useStore(modalsOpeningStore, (state) => state.loginCallback);
  const { handleJump } = useProgressiveModal<StepKeys>();
  const { startLoading, stopLoading, isLoading } = useLoading();
  const { htmlElRef, setFocus } = useFocus();
  const { toast } = useToast();
  const analytics = getAnalyticsPlugin();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const handleForgotPassword = () => {
    analytics.track(LoginAnalyticsEnum.LOGIN_CLICK_FORGOT_PASSWORD);
    handleJump({ key: StepKeys.FORGOT_PASSWORD_CPF });
  };

  const onValid = async ({ documentNumber, password }: LoginForm) => {
    try {
      startLoading();

      const isPendingEmailValidationResponse = await isPendingEmailValidation(documentNumber, password);
      if (
        isPendingEmailValidationResponse
        && isPendingEmailValidationResponse.isPendingValidation
        && !isPendingEmailValidationResponse.isProfessional
      ) {
        const formData: CreateAccountForm = {
          documentNumber,
          password,
          email: isPendingEmailValidationResponse?.customer?.email,
          cellphone: isPendingEmailValidationResponse?.customer?.phone,
          legalName: isPendingEmailValidationResponse?.customer?.name,
          socialName: isPendingEmailValidationResponse?.customer?.socialName || '',
          birthdate: isPendingEmailValidationResponse?.customer?.birthday
            ? formatISO8601toBRDate(isPendingEmailValidationResponse.customer.birthday)
            : '',
          city: `${isPendingEmailValidationResponse?.customer?.location?.city} - ${isPendingEmailValidationResponse?.customer?.location?.state}`,
        };

        setRegistrationStorage({
          formData,
          maskedEmail: isPendingEmailValidationResponse.maskedEmail,
          message: '',
          hideSocialName: true,
          token: isPendingEmailValidationResponse.token,
        });

        handleJump({ key: StepKeys.PIN_CONFIRMATION });
        return;
      }
      const loginResponse = await login(documentNumber, password);

      if (!loginResponse.access_token) {
        analytics.track(LoginAnalyticsEnum.LOGIN_ERROR, {
          error_type: LoginErrorType.CREDENCIAIS_INVALIDAS,
        });
        toast.error('CPF ou senha incorretos. Tente novamente.');
        return;
      }

      const { access_token, refresh_token } = loginResponse;
      setAuthTokens({ access_token, refresh_token });

      analytics.track(LoginAnalyticsEnum.LOGIN_SUCCESS);
      setOpenSignInModal(false);
      await resolveAuthenticatedUser(access_token);
      loginCallback?.();
    } catch (error) {
      const errorType = error instanceof HttpException && error.status === 401
        ? LoginErrorType.CREDENCIAIS_INVALIDAS
        : LoginErrorType.SERVIDOR;

      analytics.track(LoginAnalyticsEnum.LOGIN_ERROR, { error_type: errorType });
      toast.error('CPF ou senha incorretos. Tente novamente.');
    } finally {
      stopLoading();
    }
  };

  const onInvalid = (errors: FieldErrors<LoginForm>) => {
    if (errors.documentNumber) {
      analytics.track(LoginAnalyticsEnum.LOGIN_INVALID_CPF);
    }
    if (errors.password) {
      analytics.track(LoginAnalyticsEnum.LOGIN_INVALID_PASSWORD);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    analytics.track(LoginAnalyticsEnum.LOGIN_CLICK_SUBMIT);
    return form.handleSubmit(onValid, onInvalid)(event);
  };

  useEffect(() => {
    setFocus();
  }, []);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} aria-label="form">
        <FormField
          control={form.control}
          name="documentNumber"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>CPF</FormLabel>
              <FormControl>
                <Input
                  data-testid={DataTestIdEnum.LOGIN_INPUT_CPF}
                  placeholder="000.000.000-00"
                  mask="999.999.999-99"
                  {...field}
                  inputMode="numeric"
                  ref={htmlElRef}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Senha</FormLabel>
              <FormControl>
                <Input
                  data-testid={DataTestIdEnum.LOGIN_INPUT_PASSWORD}
                  autoFocus
                  placeholder="Digite a sua senha"
                  type="password"
                  maxLength={20}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="button"
          color="primary"
          variant="text"
          className="mt-4 justify-start"
          onClick={handleForgotPassword}
        >
          Esqueci minha senha
        </Button>

        <Button
          data-testid={DataTestIdEnum.LOGIN_SUBMIT_BUTTON}
          isLoading={isLoading}
          type="submit"
          color="primary"
          variant="default"
          className="mt-6 w-full h-12"
        >
          Entrar
        </Button>
      </form>
    </Form>
  );
}
